import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { endRepView, isRepSurfacePath, useActAsRep } from '../api/actAs';

/**
 * Persistent banner shown whenever an admin is acting as a rep. Renders
 * nothing otherwise. Only visible on the field surface (/regional-sales/*)
 * because the act-as context only scopes /api/field/* requests — on admin,
 * profile, or analytics pages the admin is acting in their own capacity and
 * the "writes land in the rep's account" warning would be misleading.
 */
export function ActingAsBanner() {
  const actAs = useActAsRep();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  if (!actAs) return null;
  // Show on the rep field surface (/regional-sales/*) and the admin
  // act-as view (/admin/regional-sales/reps/:id/view). Hide everywhere
  // else (admin mgmt pages, profile, analytics) where act-as has no effect.
  // Shared with the API client, so the banner is guaranteed to be up
  // on exactly the pages where impersonation is in effect.
  if (!isRepSurfacePath(pathname)) return null;

  const exit = () => {
    // Clears the rep AND their cached data — including Team Chat,
    // which this used to leave behind.
    endRepView(queryClient);
    navigate('/admin/regional-sales');
  };

  return (
    <Box
      role="status"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1150,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        px: { xs: 2, md: 3 },
        py: 1,
        color: '#5a3d12',
        background: 'linear-gradient(180deg, #fbeecb 0%, #f6e2b6 100%)',
        borderBottom: '1px solid rgba(187, 140, 63, 0.5)',
      }}
    >
      <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#8a6417' }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Acting as {actAs.name}</Typography>
      <Typography sx={{ fontSize: 12.5, color: '#6b5220', flex: 1, minWidth: 180 }}>
        You’re seeing exactly what this rep sees. Anything you log, pin, flag, add, or send is saved
        to their account (and audit-logged to you).
      </Typography>
      <Button
        size="small"
        startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
        onClick={exit}
        sx={{
          flexShrink: 0,
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'none',
          color: '#5a3d12',
          border: '1px solid rgba(187, 140, 63, 0.55)',
          borderRadius: 999,
          px: 1.5,
          bgcolor: 'rgba(255,255,255,0.5)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.8)' },
        }}
      >
        Exit rep view
      </Button>
    </Box>
  );
}
